import type { Result } from '@avalara/types/index';
import promisePoller from 'promise-poller';

import type { RouteComponentProps } from 'react-router-dom';
import conf from '../../conf';
import { setProvisioningDelayed } from '../../store/offer';
import analytics from '../analytics';
import fetch from '../fetch';
import { embeddedViewRequested } from '../helpers';
import { getSystemErrorMessaging } from './error';
import { goToNextOfferStep } from './offer-flow';

import type { Dispatch } from '@avalara/types/actions';
import type { State } from '@avalara/types/reducer-states';
import type { OfferPlan, SoldOffers } from '../../../../types/offers';

type CheckFunction<T> = (result: Result<T>) => boolean;

const checkForProvisioning = async <T = unknown>(
  dispatch: Dispatch,
  check: CheckFunction<T>,
  url: string,
): Promise<'success' | 'failed' | null> => {
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const result: Result<any> = await fetch(dispatch, { showLoadingWheel: false })(url);

  if (!result.ok || result.value.unauthenticatedUser) return 'failed';
  return check(result) ? 'success' : null;
};

const FIVE_SECONDS = 5000;
const TWO_MINUTES = 120000;
const THREE_MINUTES = 180000;
const pollProvisioningCheck =
  <T = unknown>(url: string, check: CheckFunction<T>, history: RouteComponentProps['history']) =>
  async (dispatch: Dispatch, getState: () => State) => {
    const {
      account: { accountId },
      offer: { offerFlow },
    } = getState();

    if (!offerFlow) {
      dispatch(getSystemErrorMessaging({ errorCode: 'genericError' }));
      return;
    }

    const checkResultPoll = promisePoller({
      interval: FIVE_SECONDS,
      // continue until we get an error or result
      shouldContinue: (reason, value) => !reason && !value,
      taskFn: () => checkForProvisioning<T>(dispatch, check, url),
    });

    let hasProvisioned = await Promise.race([
      checkResultPoll,
      new Promise<null>((resolve) => {
        setTimeout(() => resolve(null), THREE_MINUTES);
      }),
    ]);

    // if obligation has not provisioned within 3 minutes, update UI to indicate that it's taking
    // longer than expected
    if (!hasProvisioned) {
      analytics.track('PROVISIONING_DELAYED', {
        accountId,
        offerId: offerFlow,
      });

      dispatch(setProvisioningDelayed(true));
    }

    hasProvisioned = await Promise.race([
      checkResultPoll,
      new Promise<null>((resolve) => {
        setTimeout(() => resolve(null), TWO_MINUTES);
      }),
    ]);

    // if obligation or delivery system still has not provisioned after another 2 minutes
    // (5 minutes total), or if the obligations fetch returned an error, log that
    // the offer check has failed
    if (hasProvisioned !== 'success') {
      analytics.track('OFFER_CHECK_FAILURE', {
        accountId,
        offerId: offerFlow,
      });

      if (hasProvisioned === 'failed') {
        dispatch(getSystemErrorMessaging({ errorCode: 'genericError' }));
      }

      if (hasProvisioned == null) {
        dispatch(getSystemErrorMessaging({ errorCode: 'ips-api-error' }));
        return;
      }
    }

    if (embeddedViewRequested()) {
      window.parent.postMessage(
        {
          event: 'add-offer',
          success: hasProvisioned === 'success',
          type: offerFlow,
        },
        '*',
      );
    }

    // continue polling indefinitely until it returns a success or failure
    hasProvisioned = await checkResultPoll;

    if (hasProvisioned === 'success') dispatch(goToNextOfferStep({ history }));
  };

export const pollForDeliverySystem = (
  system: string,
  history: RouteComponentProps['history'],
  provisioningStatusId?: string,
) =>
  pollProvisioningCheck<{ provisioned: boolean }>(
    `${conf.API_URL}/api/provisioning-status/${provisioningStatusId}/delivery-systems/${system}`,
    (result) => result.ok && result.value.provisioned,
    history,
  );

export const pollForOfferPlan = (
  offerFlow: string,
  selectedPlanName: string,
  history: RouteComponentProps['history'],
) =>
  pollProvisioningCheck<Record<string, OfferPlan>>(
    `${conf.API_URL}/api/accounts/offers/${offerFlow}/status`,
    (result) => result.ok && !!result.value?.[selectedPlanName],
    history,
  );

export const pollForObligationWithConnectorId = (connectorId: string, history: RouteComponentProps['history']) =>
  pollProvisioningCheck<boolean>(
    `${conf.API_URL}/api/accounts/connector/${connectorId}/status`,
    (result) => result.ok && result.value,
    history,
  );

export const pollForSoldOffer = (accountId: string | number, history: RouteComponentProps['history']) =>
  pollProvisioningCheck<SoldOffers[]>(
    `${conf.API_URL}/api/ips/${accountId}/soldOffers`,
    (result) => result.ok,
    history,
  );
